/**
 * Chart configuration + change/reading computation for the Dashboard graph tab.
 *
 * IMPORTANT — change calculation:
 *   change = reading - initialValue + preExistingValue
 * where `initialValue` is the checkpoint's configured initial reading from the
 * `initial_*` columns on monitoring_devices (falling back to the first reading
 * in range when unset), and `preExistingValue` comes from
 * `monitoring_devices.pre_existing_value` (`pre_existing_value2` for crack Y).
 * Movement lateral distance is prepared by the API from northing/easting changes
 * plus `pre_existing_value2`/`pre_existing_value3` component offsets.
 */

import type { DashboardDevice, DashboardReading } from "./apiSlice";
import type { MonitoringDevice } from "../../types/domain";
import { isMovementType } from "../../utils/movementTypes";
import dayjs from "dayjs";

export type MetricMode = "change" | "reading";
export type CrackAxis = "x" | "y";

export interface ChartTypeConfig {
  key: string;
  label: string;
  hasContour: boolean;
  /** vibration has no change concept */
  hasChange: boolean;
  hasReading: boolean;
  changeUnit: string;
  readingUnit: string;
  /** crack renders separate X and Y series */
  series: "single" | "xy";
  /** tilting + distortion are plotted as slope ratio with an inverted y-axis */
  invertY?: boolean;
  /** vibration is plotted as discontinuous markers */
  markersOnly?: boolean;
  /** settlement sign convention remark */
  signNote?: string;
  /** settlement distortion uses the distortion-pairs time-series endpoint */
  isDistortion?: boolean;
  /** only the contour view is offered (no line chart / data table) */
  contourOnly?: boolean;
  /** plotly hover number format for this type */
  hoverFormat: string;
  /** which metric modes the LINE CHART offers (the table always shows both) */
  lineModes: MetricMode[];
  /** for "xy" series: the two sub-series and their display labels */
  axes?: { key: CrackAxis; label: string }[];
  /** draw alert lines mirrored at +value and -value (movement) */
  mirrorAlert?: boolean;
}

export const CHART_TYPES: ChartTypeConfig[] = [
  {
    key: "ground_movement",
    label: "Ground Movement",
    lineModes: ["change"],
    hasContour: true,
    hasChange: true,
    hasReading: false,
    changeUnit: "mm",
    readingUnit: "mm",
    series: "xy",
    axes: [
      { key: "x", label: "Settlement" },
      { key: "y", label: "Hor. Movement" },
    ],
    mirrorAlert: true,
    hoverFormat: ".2f",
  },
  {
    key: "building_movement",
    label: "Building Movement",
    lineModes: ["change"],
    hasContour: true,
    hasChange: true,
    hasReading: false,
    changeUnit: "mm",
    readingUnit: "mm",
    series: "xy",
    axes: [
      { key: "x", label: "Settlement" },
      { key: "y", label: "Hor. Movement" },
    ],
    mirrorAlert: true,
    hoverFormat: ".2f",
  },
  {
    key: "utility_movement",
    label: "Utility Movement",
    lineModes: ["change"],
    hasContour: true,
    hasChange: true,
    hasReading: false,
    changeUnit: "mm",
    readingUnit: "mm",
    series: "xy",
    axes: [
      { key: "x", label: "Settlement" },
      { key: "y", label: "Hor. Movement" },
    ],
    mirrorAlert: true,
    hoverFormat: ".2f",
  },
  {
    key: "movement",
    label: "Movement",
    lineModes: ["change"],
    hasContour: true,
    hasChange: true,
    hasReading: false,
    changeUnit: "mm",
    readingUnit: "mm",
    series: "xy",
    axes: [
      { key: "x", label: "Settlement" },
      { key: "y", label: "Hor. Movement" },
    ],
    mirrorAlert: true,
    hoverFormat: ".2f",
  },
  {
    key: "ground_water",
    label: "Ground Water",
    lineModes: ["change", "reading"],
    hasContour: true,
    hasChange: true,
    hasReading: true,
    changeUnit: "m",
    readingUnit: "mPD",
    series: "single",
    hoverFormat: ".3f",
  },
  {
    key: "crack",
    label: "Crack Gauge",
    lineModes: ["change"],
    hasContour: false,
    hasChange: true,
    hasReading: true,
    changeUnit: "mm",
    readingUnit: "mm",
    series: "xy",
    hoverFormat: ".2f",
  },
  {
    key: "tilting",
    label: "Tilting",
    lineModes: ["change"],
    hasContour: false,
    hasChange: true,
    hasReading: true,
    changeUnit: "1:N",
    readingUnit: "1:N",
    series: "single",
    invertY: true,
    hoverFormat: ".0f",
  },
  {
    key: "vibration",
    label: "Vibration",
    lineModes: ["reading"],
    hasContour: false,
    hasChange: false,
    hasReading: true,
    changeUnit: "mm/s",
    readingUnit: "mm/s",
    series: "single",
    markersOnly: true,
    hoverFormat: ".3f",
  },
  {
    key: "settlement_distortion",
    label: "Projected Angular Distortion",
    lineModes: ["change"],
    hasContour: false,
    hasChange: true,
    hasReading: true,
    changeUnit: "1:N",
    readingUnit: "1:N",
    series: "single",
    invertY: true,
    isDistortion: true,
    hoverFormat: ".0f",
  },
];

export const CHART_COLORS = [
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#f59e0b",
  "#7c3aed",
  "#0ea5e9",
  "#db2777",
  "#0d9488",
  "#ca8a04",
  "#4f46e5",
];

// Golden angle (~137.5°) maximises hue distance between consecutive overflow colors.
export function colorForIndex(index: number): string {
  if (index < CHART_COLORS.length) return CHART_COLORS[index];
  const hue = Math.round((index * 137.508) % 360);
  const sat = 55 + (index % 3) * 10; // cycles 55 | 65 | 75 %
  const lit = 38 + (index % 2) * 12; // cycles 38 | 50 %
  return `hsl(${hue}, ${sat}%, ${lit}%)`;
}

export function getTypeConfig(key: string): ChartTypeConfig {
  return CHART_TYPES.find((t) => t.key === key) ?? CHART_TYPES[0];
}

export function unitFor(config: ChartTypeConfig, mode: MetricMode): string {
  return mode === "change" ? config.changeUnit : config.readingUnit;
}

// ---- tilting slope-ratio helpers (ported from front/) ----

/** Convert N (the N in a 1:N slope ratio) to its angle in radians. */
export function nToRadian(n: number): number {
  if (!n || n === 0) return 0;
  return Math.atan(1 / Math.abs(n));
}

/** Convert a change-in-radian back to the signed N of a 1:N slope ratio. */
export function changeRadToOneOverN(changeRad: number): number {
  let result = 1 / 20000;
  if (changeRad > 0) {
    const tanValue = Math.tan(changeRad);
    if (tanValue > 0) result = tanValue;
  }
  return Math.round(1 / result);
}

/**
 * Movement-mode tilting: derive the slope ratio N from a movement (mm) and a
 * constant height (m): N = round(|height| * 1000 / |movement|). Returns 0 when
 * inputs are missing/invalid.
 */
export function movementToSlopeN(
  movementMm: number | null | undefined,
  heightM: number | null | undefined,
): number {
  const m = Math.abs(Number(movementMm));
  const h = Math.abs(Number(heightM));
  if (!m || !h || !isFinite(m) || !isFinite(h)) return 0;
  return Math.round((h * 1000) / m);
}

export interface SeriesPoint {
  date: string; // ISO datetime
  value: number;
}

export interface DeviceMeta {
  preExisting: number; // pre_existing_value
  preExisting2: number; // pre_existing_value2 (crack Y / movement northing)
  preExisting3: number; // pre_existing_value3 (movement easting)
  alertProfileId: number | null;
  lateralAlertProfileId: number | null; // movement lateral profile
  // Configured change baselines from monitoring_devices' initial_* columns.
  initialX: number | null; // settlement / ground water / crack X / tilting
  initialY: number | null; // crack Y / movement lateral baseline (distance)
  initialNorthing: number | null; // movement northing baseline
  initialEasting: number | null; // movement easting baseline
  initialDate: string | null; // installation_date (baseline date)
}

function finiteOrNull(v: unknown): number | null {
  const n = Number(v);
  return v != null && Number.isFinite(n) ? n : null;
}

/**
 * The configured initial value (change baseline) for a device/axis, taken from
 * the `initial_*` columns of monitoring_devices — mirroring the report/chart
 * backend. Returns null when no initial applies (e.g. movement lateral distance,
 * which is already a displacement from its own baseline).
 */
function deviceInitial(d: MonitoringDevice, axis: CrackAxis): number | null {
  switch (d.device_type) {
    case "ground_settlement":
    case "building_settlement":
    case "utility_settlement":
    case "movement":
    case "ground_movement":
    case "building_movement":
    case "utility_movement":
      return axis === "y"
        ? finiteOrNull(d.initial_distance_reading)
        : finiteOrNull(d.initial_settlement_reading);
    case "ground_water":
      return finiteOrNull(d.initial_water_level_reading);
    case "crack":
      return axis === "y"
        ? finiteOrNull(d.initial_crack_y_reading)
        : finiteOrNull(d.initial_crack_x_reading);
    case "tilting":
      // Movement entry mode derives the initial slope ratio N from the constant
      // height; slope-ratio mode stores N directly.
      if (d.tilt_entry_mode === "movement") {
        const initialMovement = finiteOrNull(d.initial_tilt_movement_reading);
        // New tilting checkpoints can have no stored movement baseline yet.
        // Returning null enables the first-reading fallback path.
        return initialMovement == null
          ? null
          : movementToSlopeN(initialMovement, d.tilt_height);
      }
      return finiteOrNull(d.initial_tilt_reading);
    default:
      return null;
  }
}

/** Build a lookup of device meta (pre-existing + initial + alert profile) keyed by device name. */
export function buildDeviceMeta(
  devices: MonitoringDevice[],
): Map<string, DeviceMeta> {
  const map = new Map<string, DeviceMeta>();
  devices.forEach((d) => {
    // Movement-mode tilting stores a pre-existing movement (mm); its effective
    // pre-existing slope ratio N is derived from the constant height.
    const preExisting =
      d.device_type === "tilting" && d.tilt_entry_mode === "movement"
        ? movementToSlopeN(d.tilt_pre_existing_movement, d.tilt_height)
        : Number(d.pre_existing_value ?? 0) || 0;
    map.set(d.device_name, {
      preExisting,
      preExisting2: Number(d.pre_existing_value2 ?? 0) || 0,
      preExisting3: Number(d.pre_existing_value3 ?? 0) || 0,
      alertProfileId: d.alert_profile_id ?? null,
      lateralAlertProfileId: d.movement_lateral_alert_profile_id ?? null,
      initialX: deviceInitial(d, "x"),
      initialY: deviceInitial(d, "y"),
      initialNorthing: finiteOrNull(d.initial_northing_reading),
      initialEasting: finiteOrNull(d.initial_easting_reading),
      initialDate: d.installation_date ?? null,
    });
  });
  return map;
}

/** Extract the raw reading value for a device type / crack axis. */
function rawReadingValue(
  r: DashboardReading,
  typeKey: string,
  axis: CrackAxis,
): number | null {
  if (typeKey === "vibration")
    return r.maxParticleAcceleration ?? r.value ?? null;
  if (typeKey === "tilting") return r.inclinationReading ?? r.value ?? null;
  if (typeKey === "crack")
    return axis === "x"
      ? (r.crackWidthXChange ?? r.value ?? null)
      : (r.crackWidthYChange ?? r.crackWidthY ?? null);
  if (isMovementType(typeKey))
    return axis === "x"
      ? (r.movementSettlement ?? null)
      : (r.movementDistance ?? null);
  return r.value ?? null;
}

/**
 * Build a per-device time series of {date, value} for the chosen metric mode.
 * Uses the device's configured initial value (monitoring_devices) as the
 * baseline for "change", falling back to the first reading in range when unset.
 */
export function buildDeviceSeries(
  device: DashboardDevice,
  config: ChartTypeConfig,
  mode: MetricMode,
  axis: CrackAxis,
  meta: DeviceMeta | undefined,
): SeriesPoint[] {
  const sorted = [...device.readings]
    .filter((r) => rawReadingValue(r, config.key, axis) != null)
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  if (!sorted.length) return [];

  // Change baseline = the device's configured initial value from
  // monitoring_devices; fall back to the first reading in range when unset.
  const actualFirst = rawReadingValue(sorted[0], config.key, axis) as number;
  const metaInitial = axis === "y" ? meta?.initialY : meta?.initialX;
  const first =
    metaInitial != null && Number.isFinite(metaInitial)
      ? metaInitial
      : actualFirst;
  const preExisting =
    isMovementType(config.key) && axis === "y"
      ? 0
      : config.key === "crack" && axis === "y"
        ? (meta?.preExisting2 ?? 0)
        : (meta?.preExisting ?? 0);

  return sorted.map((r) => {
    const raw = rawReadingValue(r, config.key, axis) as number;
    let value: number;
    if (mode === "reading") {
      value = raw;
    } else {
      switch (config.key) {
        case "ground_settlement":
        case "building_settlement":
        case "utility_settlement":
          // mPD -> mm
          value = (raw - first + preExisting) * 1000;
          break;
        case "ground_water":
          value = raw - first + preExisting;
          break;
        case "crack":
          value = raw - first + preExisting;
          break;
        case "tilting": {
          const changeRad =
            nToRadian(raw) - nToRadian(first) + nToRadian(preExisting);
          value = Math.abs(changeRadToOneOverN(changeRad));
          break;
        }
        case "movement":
        case "ground_movement":
        case "building_movement":
        case "utility_movement":
          // axis x = settlement (mPD -> mm change); axis y = lateral distance
          // (already a displacement in mm from the first reading).
          value = axis === "x" ? (raw - first + preExisting) * 1000 : raw;
          break;
        default:
          value = raw - first + preExisting;
      }
    }
    return { date: r.timestamp, value };
  });
}

const SETTLEMENT_KEYS = new Set([
  "ground_settlement",
  "building_settlement",
  "utility_settlement",
]);

const SPLIT_HORIZONTAL_MOVEMENT_KEYS = new Set([
  "ground_movement",
  "building_movement",
  "utility_movement",
]);

export function isSettlementKey(key: string): boolean {
  return SETTLEMENT_KEYS.has(key);
}

/**
 * A device column for the grouped data table (R.L + Diff per device).
 * `initialReading`/`initialDate` are the checkpoint baseline (not range-filtered).
 * `preExisting` is already converted to the display unit (settlement → mm).
 */
export interface GroupedColumn {
  name: string;
  initialReading: number | null;
  initialDate: string | null;
  preExisting: number;
  readingByDay: Map<string, number>;
  changeByDay: Map<string, number>;
  isHorizontalMovement?: boolean;
  secondaryReadingByDay?: Map<string, number>;
  secondaryInitialReading?: number | null;
  primaryReadingLabel?: string;
  secondaryReadingLabel?: string;
}

export function buildGroupedColumn(
  device: DashboardDevice,
  config: ChartTypeConfig,
  axis: CrackAxis,
  meta: DeviceMeta | undefined,
  name: string,
  from?: string,
  to?: string,
): GroupedColumn {
  const inRange = (date: string) => {
    const day = dayjs(date).format("YYYY-MM-DD");
    return (!from || day >= from) && (!to || day <= to);
  };

  const readingPts = buildDeviceSeries(device, config, "reading", axis, meta);
  const changePts = buildDeviceSeries(device, config, "change", axis, meta);

  // Display the configured initial (monitoring_devices) as the baseline; fall
  // back to the first reading in range when unset.
  const metaInitial = axis === "y" ? meta?.initialY : meta?.initialX;
  const hasMetaInitial = metaInitial != null && Number.isFinite(metaInitial);
  let initialReading = hasMetaInitial
    ? metaInitial
    : readingPts.length
      ? readingPts[0].value
      : null;
  let initialDate =
    hasMetaInitial && meta?.initialDate
      ? dayjs(meta.initialDate).format("YYYY-MM-DD")
      : readingPts.length
        ? dayjs(readingPts[0].date).format("YYYY-MM-DD")
        : null;
  let secondaryInitialReading: number | null | undefined;

  const peRaw =
    config.key === "crack" && axis === "y"
      ? (meta?.preExisting2 ?? 0)
      : isMovementType(config.key) && axis === "y"
        ? Math.sqrt(
            (meta?.preExisting2 ?? 0) * (meta?.preExisting2 ?? 0) +
              (meta?.preExisting3 ?? 0) * (meta?.preExisting3 ?? 0),
          )
        : (meta?.preExisting ?? 0);
  const preExisting =
    isSettlementKey(config.key) || (isMovementType(config.key) && axis === "x")
      ? peRaw * 1000
      : peRaw;

  const readingByDay = new Map<string, number>();
  readingPts
    .filter((p) => inRange(p.date))
    .forEach((p) =>
      readingByDay.set(dayjs(p.date).format("YYYY-MM-DD"), p.value),
    );

  const changeByDay = new Map<string, number>();
  changePts
    .filter((p) => inRange(p.date))
    .forEach((p) =>
      changeByDay.set(dayjs(p.date).format("YYYY-MM-DD"), p.value),
    );

  let secondaryReadingByDay: Map<string, number> | undefined;
  let primaryReadingLabel: string | undefined;
  let secondaryReadingLabel: string | undefined;
  const isHorizontalMovement =
    SPLIT_HORIZONTAL_MOVEMENT_KEYS.has(config.key) && axis === "y";

  // Horizontal movement tables show directional readings as Northing/Easting,
  // while keeping Diff as the existing movement-distance metric.
  if (isHorizontalMovement) {
    const northingByDay = new Map<string, number>();
    const eastingByDay = new Map<string, number>();
    const directionalReadings = [...device.readings]
      .filter((r) => r.movementNorthing != null && r.movementEasting != null)
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp));

    // For split horizontal movement, initial values must come from
    // monitoring_devices baseline fields.
    initialReading =
      meta?.initialNorthing != null && Number.isFinite(meta.initialNorthing)
        ? meta.initialNorthing
        : null;
    secondaryInitialReading =
      meta?.initialEasting != null && Number.isFinite(meta.initialEasting)
        ? meta.initialEasting
        : null;
    initialDate =
      meta?.initialDate != null
        ? dayjs(meta.initialDate).format("YYYY-MM-DD")
        : null;

    directionalReadings
      .filter((r) => inRange(r.timestamp))
      .forEach((r) => {
        const day = dayjs(r.timestamp).format("YYYY-MM-DD");
        northingByDay.set(day, r.movementNorthing as number);
        eastingByDay.set(day, r.movementEasting as number);
      });

    if (directionalReadings.length > 0) {
      readingByDay.clear();
      northingByDay.forEach((value, day) => readingByDay.set(day, value));
      secondaryReadingByDay = eastingByDay;
      primaryReadingLabel = "Northing";
      secondaryReadingLabel = "Easting";
    }
  }

  return {
    name,
    initialReading,
    initialDate,
    preExisting,
    readingByDay,
    changeByDay,
    isHorizontalMovement,
    secondaryReadingByDay,
    secondaryInitialReading,
    primaryReadingLabel,
    secondaryReadingLabel,
  };
}
